import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  Signal,
  viewChild,
  viewChildren,
} from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { unique, uniqueBy } from '@fcom/core/utils/utils';
import { FareFamily } from '@fcom/dapi/api/models/fare-family';
import { TranslatedBenefitRow } from '@fcom/dapi/api/models/translated-benefit-row';
import { ScrollHandleContainerComponent } from '@fcom/common/components/scroll-handle-container/scroll-handle-container.component';

import { shouldDisplayWifiBenefit } from '../../../utils/benefit.utils';

export interface FareFamilyWithMappedBenefits extends FareFamily {
  benefits: Record<string, TranslatedBenefitRow>;
}

@Component({
  selector: 'fin-fare-family-table',
  styleUrls: ['./fare-family-table.component.scss'],
  templateUrl: './fare-family-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FareFamilyTableComponent {
  readonly SvgLibraryIcon = SvgLibraryIcon;

  readonly scrollContainer: Signal<ScrollHandleContainerComponent> = viewChild('scrollContainer');
  readonly tableHeaders: Signal<readonly ElementRef[]> = viewChildren('tableHeader', { read: ElementRef });

  readonly showIcons = input(true);
  readonly showKoreaNotification = input<boolean>();
  readonly fareFamilies = input<FareFamily[]>();
  readonly hasWideBodyAircraft = input(false);

  readonly fareFamiliesWithMappedBenefits = computed<FareFamilyWithMappedBenefits[]>(() => {
    const fareFamilies = this.fareFamilies();
    const hasWideBodyAircraft = this.hasWideBodyAircraft();
    if (!fareFamilies || !fareFamilies[0]) {
      return [];
    }
    return fareFamilies
      .map((fareFamily) => ({
        ...fareFamily,
        benefits: fareFamily.translatedBenefitRows
          .filter((benefitRow) => shouldDisplayWifiBenefit(benefitRow, hasWideBodyAircraft))
          .reduce<Record<string, TranslatedBenefitRow>>(
            (acc, benefitRow) => ({ ...acc, [benefitRow.key]: benefitRow }),
            {}
          ),
      }))
      .filter(uniqueBy((fareFamily) => fareFamily.fareFamilyCode));
  });

  readonly benefitKeys = computed<string[]>(() => {
    const fareFamilies = this.fareFamilies();
    const hasWideBodyAircraft = this.hasWideBodyAircraft();
    if (!fareFamilies?.length) {
      return [];
    }
    const fareFamily = fareFamilies[0];
    const allBenefitKeys = fareFamily.translatedBenefitRows
      .filter((benefitRow) => shouldDisplayWifiBenefit(benefitRow, hasWideBodyAircraft))
      .map((benefitRow) => benefitRow.key);
    const benefitKeys = fareFamily.highlightedBenefitKeys
      ? [...fareFamily.highlightedBenefitKeys, ...allBenefitKeys].filter(unique)
      : allBenefitKeys;
    return benefitKeys.filter((key) => shouldDisplayWifiBenefit({ key } as TranslatedBenefitRow, hasWideBodyAircraft));
  });

  constructor() {
    effect(() => {
      if (!this.fareFamilies()?.[0]) {
        return;
      }
      this.scrollContainer()?.updateScrollHandles();
    });
  }
}
